import React, { useRef, useState } from 'react';

// image upload with drag drop support
function ImageUpload({ onPublish }) {
  const fileInput = useRef(null);
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // handle file selection
  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));

    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        // create image object
        const newImage = {
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          src: e.target.result,
          name: file.name,
          title: 'Post #' + Date.now(),
          caption: 'A beautiful moment captured',
          uploadedAt: new Date().toISOString()
        };
        setImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  // drag drop handlers
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // publish images to feed
  const publishImages = () => {
    if (images.length === 0) return;
    onPublish(images);
    setImages([]); // clear after publish
  };

  // remove single image from preview
  const removeImage = (id) => {
    setImages(images.filter(img => img.id !== id));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Images</h2>

      {/* drop zone */}
      <div
        onClick={() => fileInput.current.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}
      >
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          className="hidden"
        />
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl
          ${isDragging ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
          +
        </div>
        <p className="text-gray-600">
          {isDragging ? 'Drop images here' : 'Click or drag images to upload'}
        </p>
        <p className="text-gray-400 text-sm mt-2">Supports JPG, PNG, GIF, WebP</p>
      </div>

      {/* preview section */}
      {images.length > 0 && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg text-gray-700">
              Preview ({images.length} {images.length === 1 ? 'image' : 'images'})
            </h3>
            <button
              onClick={() => setImages([])}
              className="text-red-500 text-sm hover:text-red-600"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {images.map((img) => (
              <div key={img.id} className="relative group">
                <img
                  src={img.src}
                  alt={img.name}
                  className="w-full aspect-square object-cover rounded-lg"
                />
                <button
                  onClick={() => removeImage(img.id)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-sm
                    opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
                <p className="text-xs text-gray-500 mt-1 truncate">{img.name}</p>
              </div>
            ))}
          </div>

          {/* publish button */}
          <div className="mt-6 text-right">
            <button
              onClick={publishImages}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white
                font-semibold rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              Publish {images.length} {images.length === 1 ? 'Image' : 'Images'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageUpload;
